/**
 * Fetch ETH price in USD and EUR (CoinGecko public API).
 * Cached for 2 minutes to avoid rate limits.
 */

const CACHE_MS = 2 * 60 * 1000;
let cached = null;
let cacheTime = 0;

export async function getEthPriceInFiat() {
  if (cached && Date.now() - cacheTime < CACHE_MS) {
    return cached;
  }
  try {
    const res = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd,eur',
      { headers: { Accept: 'application/json' } }
    );
    if (!res.ok) throw new Error('Price fetch failed');
    const data = await res.json();
    const usd = data?.ethereum?.usd;
    const eur = data?.ethereum?.eur;
    if (typeof usd !== 'number' || typeof eur !== 'number') throw new Error('Invalid price data');
    cached = { usd, eur };
    cacheTime = Date.now();
    return cached;
  } catch (e) {
    if (cached) return cached;
    return { usd: null, eur: null };
  }
}

/**
 * Format an ETH amount with optional USD/EUR equivalent.
 * @param {number|string} ethAmount - Amount in ETH
 * @param {object} fiat - { usd, eur } price per 1 ETH (null = hide that currency)
 * @returns {{ ethText: string, fiatText: string }} - e.g. "0.01 ETH", "≈ $25 USD · €23 EUR"
 */
export function formatEthWithFiat(ethAmount, fiat = {}) {
  const num = Number(ethAmount);
  const ethText = Number.isNaN(num) ? '0 ETH' : `${num.toFixed(4)} ETH`;
  const parts = [];
  if (fiat?.usd != null) parts.push(`≈ $${(num * fiat.usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`);
  if (fiat?.eur != null) parts.push(`€${(num * fiat.eur).toLocaleString('en-EU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`);
  const fiatText = parts.length ? parts.join(' · ') : '';
  return { ethText, fiatText };
}

/**
 * Format an ETH amount with a single fiat currency.
 * @param {number|string} ethAmount - Amount in ETH
 * @param {object} fiat - { usd, eur } price per 1 ETH
 * @param {'usd'|'eur'} currency - Which fiat to show
 * @returns {string} - e.g. "≈ $25.00" or "≈ €23.00"
 */
export function formatEthWithFiatSingle(ethAmount, fiat = {}, currency = 'usd') {
  const num = Number(ethAmount);
  if (Number.isNaN(num)) return '';
  const price = currency === 'eur' ? fiat?.eur : fiat?.usd;
  if (price == null || price <= 0) return '';
  const amount = num * price;
  return currency === 'eur'
    ? `≈ €${amount.toLocaleString('en-EU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `≈ $${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Convert a fiat amount to ETH using current price.
 * @param {number|string} fiatAmount - Amount in USD or EUR
 * @param {'usd'|'eur'} currency
 * @param {{ usd: number|null, eur: number|null }} fiat - Price per 1 ETH
 * @returns {number|null} - ETH amount, or null if price not available
 */
export function fiatToEth(fiatAmount, currency, fiat) {
  const amount = Number(fiatAmount);
  if (Number.isNaN(amount) || amount <= 0) return null;
  const price = currency === 'usd' ? fiat?.usd : fiat?.eur;
  if (price == null || price <= 0) return null;
  return amount / price;
}

/**
 * Convert ETH amount to fiat for display/input.
 * @param {number|string} ethAmount - Amount in ETH
 * @param {'usd'|'eur'} currency
 * @param {{ usd: number|null, eur: number|null }} fiat - Price per 1 ETH
 * @returns {string} - Formatted fiat amount (e.g. "10.00") or ''
 */
export function ethToFiat(ethAmount, currency, fiat) {
  const num = Number(ethAmount);
  if (Number.isNaN(num) || num <= 0) return '';
  const price = currency === 'usd' ? fiat?.usd : fiat?.eur;
  if (price == null || price <= 0) return '';
  return (num * price).toFixed(2);
}
